import React from "react";
import { useQuery } from "@apollo/client";
import { Box, CircularProgress, Typography } from "@mui/material";
import { activeUsersByPlatformReport, activeUsersByPlatformReportByRange } from "./graphql";
import { AnalyticsReportQuery } from "./analyticsTypes";
import { parseMetricValue } from "./analyticsUtils";

interface PlatformItem {
  label: string;
  value: number;
  percent: number;
}

const findHeaderIndex = (headers: string[], target: string) =>
  headers.findIndex((header) => header.toLowerCase() === target.toLowerCase());

interface PlatformShareCardProps {
  startDate?: string;
  endDate?: string;
}

export const PlatformShareCard = ({ startDate, endDate }: PlatformShareCardProps = {}) => {
  const hasRange = !!startDate && !!endDate;
  const { data, loading, error } = useQuery<AnalyticsReportQuery>(
    hasRange ? activeUsersByPlatformReportByRange : activeUsersByPlatformReport,
    hasRange ? { variables: { startDate, endDate } } : undefined
  );
  const report = data?.analyticsReport ?? null;

  const items = React.useMemo<PlatformItem[]>(() => {
    if (!report) return [];
    const dimensionIndex = Math.max(
      0,
      findHeaderIndex(report.dimensionHeaders, "platform")
    );
    const metricIndex = Math.max(
      0,
      findHeaderIndex(report.metricHeaders, "activeUsers")
    );

    const mapped = report.rows.map((row) => {
      const label = row.dimensionValues[dimensionIndex] || "Unknown";
      const value =
        metricIndex >= 0 ? parseMetricValue(row.metricValues[metricIndex]) : 0;
      return { label, value };
    });

    const total = mapped.reduce((acc, item) => acc + item.value, 0);
    return mapped
      .filter((item) => item.label.length > 0)
      .sort((a, b) => b.value - a.value)
      .map((item) => ({
        ...item,
        percent: total > 0 ? (item.value / total) * 100 : 0,
      }));
  }, [report]);

  if (loading) {
    return (
      <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Failed to load chart.
      </Typography>
    );
  }

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data available.
      </Typography>
    );
  }

  const topItems = items.slice(0, 2);
  const maxValue = Math.max(...topItems.map((item) => item.value), 0);

  return (
    <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        {topItems.map((item) => {
          const size = maxValue > 0 ? 140 + (item.value / maxValue) * 80 : 140;
          return (
            <Box
              key={item.label}
              sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                border: "2px solid #2f6fed",
                backgroundColor: "#dbe8ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {topItems.map((item) => (
          <Box key={item.label}>
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
            <Typography variant="h6">
              {item.percent.toFixed(1)}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
